import { useQuery } from '@tanstack/react-query'
import type { TextBlock, BlockType } from '@arkturian/canvas-engine'
import { CONTENT_API } from '../config'
import { authFetch } from '../api/authFetch'

/**
 * Fetch a post's structured paragraph list and map it to the
 * TextBlock-Domain shape the canvas-engine expects.
 *
 * Backed by the live REST endpoint `GET /api/v1/posts/{id}/crdt/structured`
 * (NB: under the `/crdt/` prefix, not `/doc/`). Response shape:
 * `{ post_id, version, paragraphs: [{index, text, author_id,
 * author_name, author_color, is_heading, heading_level,
 * block_id, block_type, ...}] }`.
 *
 * `block_id` and `block_type` ARE surfaced server-side (V2.8.0 / Phase 1
 * of Fieldmap). Pre-Phase-1 paragraphs may still read `block_id: null`
 * until they're touched by a write (lazy-on-write migration) — in that
 * case we fall back to the `_paragraph_N` transient id via textBlockId
 * and default `block_type` to `'doc_content'`.
 */

interface RawParagraph {
  index: number
  text: string
  author_id: string | null
  author_name: string | null
  author_color: string | null
  is_heading?: boolean
  heading_level?: number | null
  block_id?: string | null
  block_type?: BlockType | null
  // Issue #10: server-stamped paragraph birth time, ISO 8601 UTC.
  // Null on legacy paragraphs written before the stamping deploy —
  // consumers (TimelineLayouter) fall back to paragraph_index.
  created_at?: string | null
  // Issue #16: shared identifier across every paragraph produced by
  // a single REST `doc_apply_text` call. Null on WS-typed paragraphs
  // and on pre-deploy legacy — consumers treat null as own-group.
  commit_id?: string | null
  canvas_meta?: {
    x?: number
    y?: number
    width?: number
    height?: number
    collapsed?: boolean
    group_id?: string | null
  } | null
}

interface DocStructuredResponse {
  post_id: number
  version: number
  paragraphs: RawParagraph[]
}

async function fetchDocStructured(postId: number): Promise<DocStructuredResponse> {
  const url = `${CONTENT_API}/api/v1/posts/${postId}/crdt/structured`
  const res = await authFetch(url, {
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) throw new Error(`doc_get_structured: HTTP ${res.status}`)
  return res.json()
}

function paragraphToBlock(p: RawParagraph): TextBlock {
  // Read canvas_meta from server when present — this is what makes a
  // user-positioned card land where they clicked instead of falling
  // back to the masonry-stagger placement. Missing/incomplete dims
  // are normalized: server may persist only {x, y} after a drag and
  // leave width/height implicit.
  let canvasMeta: TextBlock['canvas_meta'] = null
  if (p.canvas_meta && typeof p.canvas_meta === 'object') {
    canvasMeta = {
      x: typeof p.canvas_meta.x === 'number' ? p.canvas_meta.x : 0,
      y: typeof p.canvas_meta.y === 'number' ? p.canvas_meta.y : 0,
      width: typeof p.canvas_meta.width === 'number' ? p.canvas_meta.width : 280,
      height: typeof p.canvas_meta.height === 'number' ? p.canvas_meta.height : 120,
      collapsed: p.canvas_meta.collapsed,
      group_id: p.canvas_meta.group_id ?? null,
    }
  }
  // Issue #10: forward server-stamped created_at as a structural
  // property on the block. The canvas-engine TextBlock interface
  // doesn't formally declare the field yet (FieldFront's next bump
  // adds it); we attach it here so TimelineLayouter can read it as
  // soon as the package update lands without a coordinated re-deploy.
  return {
    block_id: p.block_id ?? null,
    paragraph_index: p.index,
    block_type: p.block_type ?? 'doc_content',
    author: {
      id: p.author_id ?? 'anon',
      name: p.author_name ?? 'Anon',
      color: p.author_color ?? 'hsl(0, 0%, 70%)',
    },
    text_preview: p.text,
    char_count: p.text.length,
    in_section_id: null,
    canvas_meta: canvasMeta,
    created_at: p.created_at ?? null,
    commit_id: p.commit_id ?? null,
  } as TextBlock & {
    created_at: string | null
    commit_id: string | null
  }
}

/**
 * One paragraph = one card. Earlier iteration merged consecutive
 * same-author paragraphs (mergeConsecutiveSameAuthor) under the
 * assumption that an AI multi-paragraph reply is one Beitrag. That
 * model breaks down for canvas-mode: when a user explicitly creates
 * 4 cards by clicking "+" four times, all four merge into one because
 * they're the same author back-to-back — exactly NOT what they
 * intended.
 *
 * Each paragraph now stays its own TextBlock. Intentional grouping
 * comes later via explicit group_id on canvas_meta (server can set
 * this; UI for it is Phase 5c follow-up).
 *
 * Heading paragraphs are still filtered — they're structure, not
 * Beiträge — to keep the canvas free of standalone H1/H2 "label"
 * cards that aren't editable content.
 */
function paragraphsToBlocks(paragraphs: RawParagraph[]): TextBlock[] {
  // Filter empty paragraphs (Backspace-deleted in doc-mode leaves
  // the node behind with empty text — would render as empty cards).
  // Exception: keep canvas_meta-stamped placeholders even if empty,
  // because user may have created a card via "+" / dblclick to fill
  // later. Doc-mode renders empty paragraphs normally.
  return paragraphs
    .filter((p) => {
      if (p.is_heading) return false
      if (!p.text || p.text.trim().length === 0) {
        return p.canvas_meta != null
      }
      return true
    })
    .map(paragraphToBlock)
}

export interface UsePostBlocksResult {
  blocks: TextBlock[]
  version: number | undefined
  isLoading: boolean
  error: Error | null
}

export function usePostBlocks(postId: number | null): UsePostBlocksResult {
  const { data, isLoading, error } = useQuery<DocStructuredResponse>({
    queryKey: ['docStructured', postId],
    queryFn: () => fetchDocStructured(postId!),
    enabled: postId !== null,
    // 1.5s staleTime + 3s refetch interval gives the canvas near-live
    // sync with doc-mode edits and concurrent writes from other agents,
    // without thrashing the network. A more reactive WS-subscription
    // (invalidate on Yjs update event) is a Phase 5c follow-up.
    staleTime: 1_500,
    refetchInterval: 3_000,
    refetchOnWindowFocus: true,
  })
  const blocks = data ? paragraphsToBlocks(data.paragraphs) : []
  return {
    blocks,
    version: data?.version,
    isLoading,
    error: (error as Error) ?? null,
  }
}
